import type { CameraRig } from './camera-rig.js';
import {
  BROADCAST_PRESETS,
  getBroadcastPreset,
  type BroadcastPresetId,
  type SceneContext,
} from './broadcast-presets.js';

/**
 * F-31 Broadcast カメラモード — Director 状態機械（P2）。
 *
 * 設計書: `knowledge/architecture/broadcast-camera-mode.md` §3
 * 適用ルール: CLAUDE.md `class 禁止` → **純関数 reducer `(state, action) => state`** で実装。
 * 副作用（rig API 呼び出し）は `applyDirectorEffects(prev, next, rig, ctx)` で集約。
 *
 * **本ファイルは GPL 非依存**: `src/engine/**` / `src/render/vendor/**` を import しない。
 * `broadcast-presets.ts` および `camera-rig.ts`（型のみ）に依存。
 *
 * ## Director の役割
 *
 * 盤面状況（ラック前 / 構え / 発射 / 接近 / ポット / 静止 / 勝利）の入力を受け、
 * 適切なカット（プリセット）を選んで遷移する。むやみな切替を避けるため、
 * 同一プリセット最小保持時間（1.5 秒）と最大スイッチ頻度（0.3 Hz）の安全弁を持つ。
 *
 * Manual モード（HUD タップ・キーボード）の入力は本 reducer の外側で直接プリセットを
 * 適用するため、Director の state は更新しない（Director の自動カットは「同じプリセットを
 * 既に当てているなら何もしない」性質を持つため矛盾しない）。
 *
 * ## P2b 拡張（2026-06-26）
 *
 * 設計書 §12 に基づき、POV/POCK 偏重と「球が画面外」フィードバックへ対応するため
 * 新規 action を追加: `shot-fired-hard`（ハードショット時 LOW へ）/ `foul-detected`
 * （ファウル時 WIDE へ・MIN_HOLD バイパス）/ `idle-too-long`（POV 30 秒以上で JIB へ）/
 * `framing-lost`（主要 3 点が画面外で POV フォールバック・MIN_HOLD バイパス）。
 * `ball-pot` も拡張: 非勝利球なら短い Beauty Wide インサート（MIN_HOLD で吸収）。
 *
 * ## P3 再設計（2026-06-26 §14）
 *
 * §13 リサーチ（BBC Masters 20–22° 主役カメラ / ライブスポーツのカット禁止帯 / ポケットカム
 * 事前選定 / ball-pot 後ホールド）に基づく Director の再構築。
 *
 * 主な変更:
 * - **新主役 Broadcast Master (BM)**: `hot-head` を `broadcast-master` へ rename（短クッション 20–25°）。
 * - **カット禁止帯**: shot-fired から all-settled まで Director は新規カットを発火しない
 *   （`framing-lost` のみ安全弁として例外）。
 * - **POCK 事前選定**: `prepare-pocket-cam` action を新設。shot-fired 直前に
 *   `pendingPocketCam` を確定し、ball-pot 時に POCK へ smooth 切替する。
 * - **ball-pot 後ホールド**: `potHoldUntilMs` を state に追加。非勝利球 2.0 秒、勝利球 3.0 秒は
 *   同じカメラ（POCK）を保持してから BM へ復帰する。`tick` がホールド完了を検知し自動復帰。
 * - **shot-fired-hard / pocket-approach（per-frame）廃止**: いずれもショット中カット発火＝
 *   §13 F-2 違反のため除去。LOW は manual 専用に縮退。
 * - **phase-changed: normal/win の遷移先を BM へ**（旧 player-pov / hot-head から差替）。
 */

/** Director の現在の追従先プリセット（または `transition` 中）。 */
export type DirectorPhase = BroadcastPresetId | 'transition';

/** Director の盤面イベント入力。
 *  P3 §14.2.3 で再構成: `shot-fired-hard` / `pocket-approach`（per-frame）を撤回し、
 *  `prepare-pocket-cam`（ショット前事前選定）を新設。 */
export type DirectorAction =
  | { type: 'phase-changed'; phase: 'break' | 'normal' | 'win' }
  /** ショット発射通知。**P3.4 §14.14**: ショット発射時はカット発火しない（カット禁止帯）。
   *  inShot=true を立てるだけ。直前の BM 構図が手玉移動中も保持される。
   *  実際の POCK 切替は `first-contact`（手玉→他球の最初の collision）で行う。 */
  | { type: 'shot-fired'; isBreak: boolean }
  /** P3 §14.4: shot-fired 直前に呼び、`pendingPocketCam` を確定する（カット発火なし）。
   *  first-contact 時に POCK の構図（lookAt=target, azimuth=pocket側）を決める。 */
  | { type: 'prepare-pocket-cam'; pocketPos: { x: number; y: number } }
  /** P3.4 §14.14 新規: 手玉と他球（または的玉同士）の **最初の ball-ball collision** 検出。
   *  これが POCK 切替の本来のトリガー（旧 P3.1 の shot-fired 即カットを撤回）。
   *  pendingPocketCam があれば POCK へ force-switch、なければ現プリセット維持（ブレイク等）。
   *  cushion（クッション接触）は対象外（kick shot で先にレールに当たる場合は無視・的玉に
   *  当たった瞬間に発火）。1 ショット 1 回・呼び出し側 (main.ts) が outcomes から検出。 */
  | { type: 'first-contact' }
  /** ボールポット通知。**P3 §14**: 勝利/通常ともに Pocket Cam（force）へ smooth 切替し、
   *  `potHoldUntilMs` を `nowMs + (isWinBall ? 3000 : 2000)` にセットしてホールド開始。
   *  非勝利球も Beauty Wide ではなく POCK で「ポケットインまで見届ける」運用へ。 */
  | { type: 'ball-pot'; ballId: number; isWinBall: boolean }
  /** P2b §12.6: 9 番勝利シーケンスの 2 段目 — Pocket Cam → BM へカット。
   *  P3 §14 で旧 Hot Head 遷移を BM へ差替。呼び出し側 (main.ts) が ball-pot isWinBall=true
   *  から T+0.8 秒のタイミングで dispatch する。MIN_HOLD バイパス。 */
  | { type: 'win-sequence-tophead' }
  /** P2b §12.2: ファウル / スクラッチ検知 → Beauty Wide でリセット感。
   *  ファウルは稀かつ重要イベントのため MIN_HOLD_MS をバイパスする。
   *  **P3 §14.2.4**: IN_SHOT 中は無視（ショット中カット禁止）。all-settled 後の呼び出し前提。 */
  | { type: 'foul-detected' }
  /** P3 §14.2.4: IDLE 30 秒以上で Beauty Wide へ（旧 JIB から変更・JIB はセレモニー専用）。 */
  | { type: 'idle-too-long' }
  /** P2b §12.3: Framing Safety — 主要 3 点が画面外に出続けた。BM へフォールバック。
   *  安全弁として MIN_HOLD_MS をバイパスし、ショット中カット禁止帯の唯一の例外。
   *  P3 §14 で旧 player-pov フォールバックを BM へ差替（BM の方がフレーミングが安全）。 */
  | { type: 'framing-lost' }
  | { type: 'all-settled' }
  | { type: 'user-override-detected' }
  | { type: 'tick'; nowMs: number };

/** Director の不変な内部状態。reducer は新しい state を返す。 */
export interface DirectorState {
  /** 現在のプリセット（または遷移中）。 */
  readonly current: DirectorPhase;
  /** 最後にプリセットを切り替えた時刻（ms・ローカル単調増加・`tick` 経由）。 */
  readonly lastSwitchAtMs: number;
  /** 現在 tick が観測した最新時刻（ms）。 */
  readonly nowMs: number;
  /** ユーザー手動操作で Director を一時停止する解除時刻（ms）。
   *  この時刻以降にならないと自動カットを発火しない。 */
  readonly userOverrideUntilMs: number;
  /** ショット発射中フラグ（freezeForShot 相当の間は別経路に委ねる）。 */
  readonly inShot: boolean;
  /** 直近のフェーズ（'break' のときはブレイク中ロジック適用）。 */
  readonly phase: 'break' | 'normal' | 'win';
  /** P3 §14.2.2: ball-pot 後の hold-timer 終了時刻（ms）。
   *  この時刻まで POCK（または直前のカメラ）を保持し、新規カットを発火しない。
   *  非勝利球 2.0 秒 / 勝利球 3.0 秒。`tick` がホールド完了で BM へ自動復帰する。
   *  0 ならホールドなし（既定）。 */
  readonly potHoldUntilMs: number;
  /** P3 §14.4: shot-fired 直前に prepare-pocket-cam で確定した狙うポケット位置。
   *  ball-pot のとき POCK の構図を決めるための SceneContext.aimedPocket に流す
   *  （main.ts の applyDirectorEffects 経由）。null ならフォールバック（手玉延長線推定）。 */
  readonly pendingPocketCam: { readonly x: number; readonly y: number } | null;
}

/** Director の挙動パラメータ（安全弁・タイマー）。設計書 §3.2 / §14.2.2 に整合。 */
export const DIRECTOR_PARAMS = {
  /** 同一プリセット最小保持時間（ms）。1.5 秒。 */
  MIN_HOLD_MS: 1500,
  /** ユーザー手動操作後の Director 自動復帰待機時間（ms）。3 秒。 */
  USER_OVERRIDE_HOLD_MS: 3000,
  /** P3 §14.2.4: 通常ポット後の hold-timer（ms）。POCK を 2 秒保持。 */
  POT_HOLD_NORMAL_MS: 2000,
  /** P3 §14.2.4: 勝利球ポット後の hold-timer（ms）。POCK を 3 秒保持。 */
  POT_HOLD_WIN_MS: 3000,
} as const;

/** Director の初期状態を作る。
 *  `lastSwitchAtMs` は MIN_HOLD_MS だけ過去にセット = 起動直後の最初のイベントで即座に
 *  切替可能（「起動から 1.5 秒待たないと最初のカットが入らない」を防ぐ）。
 *  P3 §14: 初期プリセットは Broadcast Master（旧 player-pov から差替）。 */
export const initDirectorState = (nowMs = 0): DirectorState => ({
  current: 'broadcast-master',
  lastSwitchAtMs: nowMs - DIRECTOR_PARAMS.MIN_HOLD_MS,
  nowMs,
  userOverrideUntilMs: 0,
  inShot: false,
  phase: 'normal',
  potHoldUntilMs: 0,
  pendingPocketCam: null,
});

/** プリセット切替可能か判定するヘルパ（安全弁適用）。 */
const canSwitchTo = (
  state: DirectorState,
  nextPreset: BroadcastPresetId,
  now: number,
): boolean => {
  // ユーザー手動操作中は Director を一時停止
  if (now < state.userOverrideUntilMs) return false;
  // 同じプリセットなら切替不要
  if (state.current === nextPreset) return false;
  // 同一プリセット最小保持時間（前回切替からの経過）
  if (now - state.lastSwitchAtMs < DIRECTOR_PARAMS.MIN_HOLD_MS) return false;
  return true;
};

/** 強制的に next preset へ切り替えた新 state を返す。 */
const switchTo = (
  state: DirectorState,
  next: BroadcastPresetId,
): DirectorState => ({
  ...state,
  current: next,
  lastSwitchAtMs: state.nowMs,
});

/** P2b §12.2/§12.3: 高優先イベント（foul / framing-lost）の強制切替。
 *  MIN_HOLD_MS をバイパスするが、ユーザー手動操作中（userOverride）は尊重して何もしない。 */
const forceSwitchTo = (
  state: DirectorState,
  next: BroadcastPresetId,
): DirectorState => {
  if (state.nowMs < state.userOverrideUntilMs) return state;
  if (state.current === next) return state;
  return { ...state, current: next, lastSwitchAtMs: state.nowMs };
};

/**
 * Director 純関数 reducer。
 * 副作用は持たない。state と action から新しい state を返す。
 * rig API 呼び出しは呼び出し側の `applyDirectorEffects` で行う。
 *
 * P3 §14 で大幅再構築: カット禁止帯（IN_SHOT 中は新規カットを発火しない）・
 * POCK 事前選定（`prepare-pocket-cam`）・hold-timer（ball-pot 後 2-3 秒 POCK 保持）。
 */
export const reduceDirector = (
  state: DirectorState,
  action: DirectorAction,
): DirectorState => {
  switch (action.type) {
    case 'tick': {
      // P3 §14.2.4: hold-timer 完了で BM へ自動復帰（POT_HOLD → SETTLE 遷移）。
      // shot 終了済（inShot=false）かつ hold 期限を過ぎていれば BM 復帰を試みる。
      const ticked: DirectorState = { ...state, nowMs: action.nowMs };
      if (
        ticked.potHoldUntilMs > 0 &&
        action.nowMs >= ticked.potHoldUntilMs &&
        !ticked.inShot
      ) {
        // hold 完了。BM へ復帰し pendingPocketCam もクリア。
        if (canSwitchTo(ticked, 'broadcast-master', action.nowMs)) {
          return switchTo(
            { ...ticked, potHoldUntilMs: 0, pendingPocketCam: null },
            'broadcast-master',
          );
        }
        // canSwitchTo 不可（userOverride 中など）はホールド解除のみ行う。
        return { ...ticked, potHoldUntilMs: 0 };
      }
      return ticked;
    }
    case 'user-override-detected': {
      // 手動操作検知 → 3 秒間 Director を停止
      return {
        ...state,
        userOverrideUntilMs: state.nowMs + DIRECTOR_PARAMS.USER_OVERRIDE_HOLD_MS,
      };
    }
    case 'phase-changed': {
      const newState: DirectorState = { ...state, phase: action.phase };
      // P3 §14.2.4 遷移表:
      //   break  → WIDE（ラック全景・main.ts の ceremony スケジューラが 3 秒後に BM へ）
      //   normal → BM（短クッション主役）
      //   win    → POCK 保持（ball-pot で既に POCK・hold-timer 中なら no-op、tick が BM 復帰）
      let target: BroadcastPresetId;
      if (action.phase === 'break') target = 'beauty-wide';
      else if (action.phase === 'win') target = 'pocket-cam';
      else target = 'broadcast-master';
      if (!canSwitchTo(newState, target, state.nowMs)) return newState;
      return switchTo(newState, target);
    }
    case 'shot-fired': {
      // P3.4 §14.14（再改訂）: ショット発射時は **カット発火しない**（inShot=true のみ）。
      //
      // ユーザーフィードバック（2026-06-26 §14.14 議論）:
      // 「ショット直後にカメラが切り替わるのが違和感がある。的玉に当たるまでは、カメラは
      //  固定しておいて欲しい」
      //
      // P3.1 では shot-fired で POCK へ即カットしていたが、手玉が移動中（まだ的玉に
      // 当たっていない）のタイミングでカットが入るのが違和感の原因。
      // BBC 放送でも「ショット発射の瞬間はメインカメラを保持、的玉に当たった後に
      // 追従/ポケットカムへ切替」が標準。これに整合させる。
      //
      // 実際の POCK 切替は `first-contact` action（手玉と他球の最初の collision）で行う。
      // shot-fired はこのフェーズ遷移を IN_SHOT へ移すマーカーのみ。
      return { ...state, inShot: true };
    }
    case 'first-contact': {
      // P3.4 §14.14 新規: 手玉と他球の最初の collision 検出時に POCK へ force-switch。
      // pendingPocketCam が null（ブレイク・対象球無し等）の場合は現プリセット（BM）維持。
      // 1 ショット 1 回（呼び出し側 main.ts で制御）。
      if (!state.pendingPocketCam) return state;
      return forceSwitchTo(state, 'pocket-cam');
    }
    case 'prepare-pocket-cam': {
      // P3 §14.4: 事前選定。カットは発火せず pendingPocketCam を記録するのみ。
      // ball-pot 時に applyDirectorEffects がこの値を ctx.aimedPocket に流し込む。
      return { ...state, pendingPocketCam: action.pocketPos };
    }
    case 'ball-pot': {
      // P3 §14.2.4: ポット成功 → POCK へ force 切替 + hold-timer 開始。
      // 勝利/通常で hold 時間を分ける（POT_HOLD_WIN_MS=3000 / POT_HOLD_NORMAL_MS=2000）。
      // pendingPocketCam が null（手玉スクラッチ等で対象球なし）の場合も POCK は試行
      // （POCK の apply 側で aimedPocket=null → Player POV 相当へフォールバック）。
      const holdDur = action.isWinBall
        ? DIRECTOR_PARAMS.POT_HOLD_WIN_MS
        : DIRECTOR_PARAMS.POT_HOLD_NORMAL_MS;
      const potHoldUntilMs = state.nowMs + holdDur;
      const switched = forceSwitchTo(state, 'pocket-cam');
      return { ...switched, inShot: false, potHoldUntilMs };
    }
    case 'win-sequence-tophead': {
      // P3 §14: 勝利シーケンス 2 段目 — POCK → Broadcast Master（旧 Hot Head 撤回）。
      // MIN_HOLD バイパス（POCK 切替直後の 0.8s で遷移する設計）。
      // hold-timer もクリア（BM へ移ったので POCK 保持は不要）。
      const switched = forceSwitchTo(state, 'broadcast-master');
      return { ...switched, potHoldUntilMs: 0, pendingPocketCam: null };
    }
    case 'foul-detected': {
      // P3 §14.2.4: ファウル/スクラッチ → WIDE。
      // **IN_SHOT 中の dispatch は無視**（ショット中カット禁止帯）。all-settled 後の運用前提。
      if (state.inShot) return state;
      return forceSwitchTo(state, 'beauty-wide');
    }
    case 'framing-lost': {
      // P3 §14.2.4: Framing Safety フォールバック → Broadcast Master（旧 player-pov から差替）。
      // BM の方が広角・低仰角でフレーミングが安全。MIN_HOLD バイパス（最後の安全弁）。
      // ショット中カット禁止帯の **唯一の例外**（球が完全に画面外＝放置できない）。
      return forceSwitchTo(state, 'broadcast-master');
    }
    case 'idle-too-long': {
      // P3 §14.2.4: IDLE 30 秒以上 → WIDE（旧 JIB から変更）。JIB はセレモニー専用に。
      if (!canSwitchTo(state, 'beauty-wide', state.nowMs)) return state;
      return switchTo(state, 'beauty-wide');
    }
    case 'all-settled': {
      // P3 §14.2.4: 全球停止 → inShot=false。hold-timer が残っていれば BM 復帰は遅延
      // （tick が hold 完了で自動復帰）。それ以外は BM へ復帰を試みる。
      const next: DirectorState = { ...state, inShot: false };
      if (next.potHoldUntilMs > 0 && state.nowMs < next.potHoldUntilMs) {
        // POT_HOLD 中: BM 復帰せず POCK 保持
        return next;
      }
      // win phase でも BM へ復帰（旧 Hot Head 撤回）
      const target: BroadcastPresetId = 'broadcast-master';
      if (!canSwitchTo(next, target, state.nowMs)) return next;
      return switchTo(next, target);
    }
  }
};

/** P2b §12.4: プリセット遷移時の既定スムースカット時間（秒）。 */
export const SMOOTH_CUT_DURATION_SEC = 0.4;

/**
 * state 遷移後に rig 側へ副作用を反映する。
 *
 * `prev.current !== next.current` のときのみ rig へプリセット apply を呼ぶ
 * （reducer の純粋性を保ちつつ、実際のカメラ動作はここに集約）。
 *
 * **P2b §12.4 滑らかなカット**: プリセット apply の直前に `rig.smoothCutTo(0.4)` を呼び、
 * 現在の描画位置を snapshot として捕捉してから新プリセットへ補間する（瞬時切替の違和感を解消）。
 * 例外: 次が `jib-sweep` の場合は `panToKeyframes` が出力を握るため smoothCutTo は無効化される
 * （rig 側で early-return）。
 *
 * Manual モード（HUD タップ・キーボード）から `applyBroadcastPreset` を直接呼んだ場合は
 * Director state とは独立して rig が更新される。次の自動カット時に Director が「同じプリセット
 * を継続」と判定すれば衝突しない（canSwitchTo がガード）。
 */
export const applyDirectorEffects = (
  prev: DirectorState,
  next: DirectorState,
  rig: CameraRig,
  ctx: SceneContext,
): void => {
  if (prev.current === next.current) return;
  if (next.current === 'transition') return;
  const preset = getBroadcastPreset(next.current);
  if (!preset) return;
  // P3 §14.4: POCK 切替時は pendingPocketCam が ctx.aimedPocket を上書き
  // （事前選定の値を優先・main.ts の SceneContext.aimedPocket よりも Director state が
  // 「ショット軌道から狙うポケット」として確定した値を使う）。
  const effectiveCtx: SceneContext =
    next.current === 'pocket-cam' && next.pendingPocketCam
      ? { ...ctx, aimedPocket: next.pendingPocketCam }
      : ctx;
  // P3.2 §14.2.4: プリセット適用 → snap で瞬時カット化（§13 F-2 ライブスポーツ規範）。
  // 旧 P2b §12.4 の smoothCutTo(0.4 秒フェード) は §13 F-2 と矛盾していたため撤回。
  // preset.apply で rig 内部の desired/target 値を新構図にセット → snapInternalState で
  // azimuth / target / blend を即時収束 → 次フレームの update では「別カメラに切り替わった」
  // ように見える（lerp 残量ゼロ）。
  preset.apply(rig, effectiveCtx);
  rig.snapInternalState();
};

/** Director が選びうるプリセット一覧（テスト用に export）。
 *  P2 では全 6 プリセットが Director の遷移先となる。 */
export const DIRECTOR_REACHABLE_PRESETS: readonly BroadcastPresetId[] =
  BROADCAST_PRESETS.map((p) => p.id);
